/**
 * GroupRules — per-group attendance-rule overrides.
 *
 * Migrated from mock data to live API as part of Wave 3.2. The
 * backend stores group overrides as full AttendanceRule rows
 * (same schema as the global row, one per group with an
 * override). Groups without an override row inherit the global
 * configuration transparently.
 *
 * Three-view flow:
 *   List     → groups + chips showing which have an override
 *   Detail   → read-only view of the override (or globals if none)
 *                + "Add override" / "Edit override" / "Revert to global"
 *                  actions
 *   Editing  → same form layout as GlobalRules, pre-filled from the
 *                override row or global defaults as a starting point
 *
 * The field schema is shared via ruleSections.ts. Group overrides use the
 * complete RULE_SECTIONS set, including rest_days, while GlobalRules excludes
 * rest_days from its editable payload.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBackOutlined,
  Cancel,
  Edit,
  InfoOutlined,
  PeopleAltOutlined,
  RefreshOutlined,
  RestartAlt,
  Save,
} from '@mui/icons-material';
import { ApiError, attendanceApi, systemApi } from '../../../api';
import type {
  AttendanceRule,
  AttendanceRuleUpdate,
} from '../../../api/attendance';
import type { Schema } from '../../../api/types';
import { ConfirmDialog, StickyFormActions } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';
import {
  changedScoringKeys,
  DAY_OPTIONS,
  isEveryDayRest,
  isRestDaySelected,
  isWideRuleSection,
  MINUTE_OPTIONS,
  RULE_SECTIONS,
  type RuleField,
  ruleToFormValues,
} from './ruleSections';
import { RecalcNotice, RuleSaveConfirm } from './RuleSaveConfirm';

type WorkerGroup = Schema<'WorkerGroupResponse'>;

// Toggles whose ON state forces an extra step or blocks a punch — enabling any
// of these in an override shows a confirm dialog (parity with GlobalRules).
// Backend snake_case keys; the ruleConfirm.* i18n block is shared.
const HIGH_IMPACT_TOGGLES = new Set<string>([
  'require_face',
  'require_liveness',
  'gps_required',
  'enforce_geofence',
]);

const GroupRules: React.FC = () => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<WorkerGroup[]>([]);
  const [globalRule, setGlobalRule] = useState<AttendanceRule | null>(null);
  // Sparse map: only groups with an override row are present.
  const [overrides, setOverrides] = useState<Record<string, AttendanceRule>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [savedScoringKeys, setSavedScoringKeys] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // Deep-link support: /attendance/rules/group?group_id=X opens
  // the detail view for that group automatically once groups
  // finish loading. Drives the "Attendance rules" shortcut on the
  // Groups kebab menu.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkDeptId = searchParams.get('group_id');

  const [selectedDept, setSelectedDept] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [pendingRevert, setPendingRevert] = useState<WorkerGroup | null>(null);
  // Turning a high-impact toggle ON pops a confirm dialog (parity with
  // GlobalRules). Keyed by backend snake_case; null = closed.
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [deps, globalR, ovList] = await Promise.all([
        systemApi.workerGroupsApi.list(),
        attendanceApi.rulesApi.getGlobal(),
        attendanceApi.rulesApi.listGroupRules(),
      ]);
      setGroups(deps);
      setGlobalRule(globalR);
      const ovMap: Record<string, AttendanceRule> = {};
      for (const r of ovList) {
        if (r.group_id) ovMap[r.group_id] = r;
      }
      setOverrides(ovMap);
    } catch (e) {
      const msg =
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:groupRules.loadFailed');
      setError(msg);
      setGroups([]);
      setGlobalRule(null);
      setOverrides({});
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Once groups load, honour the deep-link by auto-opening the
  // detail view for the referenced dept. We also strip the param from
  // the URL so a subsequent Back button from detail doesn't re-trigger
  // the auto-open loop.
  useEffect(() => {
    if (!deepLinkDeptId || selectedDept || groups.length === 0) return;
    const exists = groups.some((d) => d.id === deepLinkDeptId);
    if (!exists) return;
    setSelectedDept(deepLinkDeptId);
    const next = new URLSearchParams(searchParams);
    next.delete('group_id');
    setSearchParams(next, { replace: true });
  }, [deepLinkDeptId, selectedDept, groups, searchParams, setSearchParams]);

  const filteredDepts = useMemo(
    () =>
      groups.filter((d) =>
        d.name.toLowerCase().includes(filterText.toLowerCase()),
      ),
    [groups, filterText],
  );
  const paginatedDepts = useMemo(
    () =>
      filteredDepts.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredDepts, page, rowsPerPage],
  );

  const selected =
    selectedDept != null
      ? groups.find((d) => d.id === selectedDept) ?? null
      : null;

  const selectedOverride =
    selectedDept != null ? overrides[selectedDept] ?? null : null;

  // Number of fields on the override that differ from the global row.
  // Stat that drives the "Customized (N)" chip on the list and helps
  // operators see how much of the override actually differs.
  const computeDivergence = useCallback(
    (ov: AttendanceRule | null) => {
      if (!ov || !globalRule) return 0;
      let diff = 0;
      for (const section of RULE_SECTIONS) {
        for (const field of section.fields) {
          const a = (ov as unknown as Record<string, unknown>)[field.key];
          const b = (globalRule as unknown as Record<string, unknown>)[
            field.key
          ];
          // Array deep equal via JSON — rest_days is the only array field
          // and it's always a small list of weekday strings.
          if (JSON.stringify(a) !== JSON.stringify(b)) diff += 1;
        }
      }
      return diff;
    },
    [globalRule],
  );

  const handleSelectDept = (id: string) => {
    setSelectedDept(id);
    const ov = overrides[id];
    // Pre-fill form with override values if any, else global defaults.
    const source = ov ?? globalRule;
    if (source) {
      setValues(
        ruleToFormValues(source as unknown as Record<string, unknown>),
      );
    }
    setIsEditing(false);
  };

  const handleBackToList = () => {
    setSelectedDept(null);
    setIsEditing(false);
    setValues({});
  };

  const handleChange = (key: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleStartEdit = () => {
    // Re-seed from current source (override or global) so entering
    // edit mode after a revert fills with globals, not stale
    // override values.
    const source = selectedOverride ?? globalRule;
    if (source) {
      setValues(
        ruleToFormValues(source as unknown as Record<string, unknown>),
      );
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    const source = selectedOverride ?? globalRule;
    if (source) {
      setValues(
        ruleToFormValues(source as unknown as Record<string, unknown>),
      );
    }
    setIsEditing(false);
  };

  // Which SCORING fields this edit would change, measured against the override currently
  // stored for this scope. Computed before the write so the confirm can name them.
  const pendingScoringChanges = changedScoringKeys(
    (selectedDept ? overrides[selectedDept] : null) as unknown as Record<string, unknown> | null,
    values,
  );

  /** Save is a two-step action: name the consequence, then write. */
  const requestSave = () => setConfirmSave(true);

  const performSave = async () => {
    if (!selectedDept) return;
    setSaving(true);
    try {
      const changed = pendingScoringChanges;
      const updated = await attendanceApi.rulesApi.setGroupRules(
        selectedDept,
        values as AttendanceRuleUpdate,
      );
      setOverrides((prev) => ({ ...prev, [selectedDept]: updated }));
      setValues(
        ruleToFormValues(updated as unknown as Record<string, unknown>),
      );
      setIsEditing(false);
      setConfirmSave(false);
      // From the pre-write snapshot: `overrides` has been replaced, so recomputing the diff
      // here would always be empty and the notice would never appear.
      setSavedScoringKeys(changed);
      setToast(t('attendance:groupRules.toastOverrideSaved', { name: selected?.name ?? '' }));
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed');
      setToast(msg);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRevert = async () => {
    if (!pendingRevert) return;
    try {
      await attendanceApi.rulesApi.deleteGroupRules(pendingRevert.id);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[pendingRevert.id];
        return next;
      });
      setToast(t('attendance:groupRules.toastRevertedToGlobal', { name: pendingRevert.name }));
      // If we're viewing the reverted dept, rehydrate from global.
      if (selectedDept === pendingRevert.id && globalRule) {
        setValues(
          ruleToFormValues(globalRule as unknown as Record<string, unknown>),
        );
      }
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('attendance:groupRules.revertFailed');
      setToast(msg);
      throw e;
    }
  };

  // ------- Field rendering (same shape as GlobalRules, different
  //          editable-state binding: "not editing" OR "field equals
  //          global" shows the control dimmed). -------

  const renderField = (field: RuleField) => {
    const v = values[field.key];
    const disabled = !isEditing;
    // Read-only: show the configured value as bold, prominent text / pill rather
    // than a grayed disabled input (owner 2026-06-30). 'days' keeps its chips.
    if (!isEditing && field.type !== 'days') {
      if (field.type === 'toggle') {
        const on = !!v;
        return (
          <Chip
            label={on ? t('attendance:ruleSections.valueOn') : t('attendance:ruleSections.valueOff')}
            size="small"
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, borderRadius: 1, ...(on ? {} : { color: 'text.disabled', borderColor: 'divider' }) }}
          />
        );
      }
      let label = '—';
      if (field.type === 'minutes') {
        const m = typeof v === 'number' ? v : 0;
        label = m >= 60 ? `${m / 60} hour${m > 60 ? 's' : ''}` : `${m} min`;
      } else if (field.type === 'select') {
        const opt = field.options?.find((o) => String(o.value) === String(v));
        label = opt ? t(`attendance:ruleSections.options.${opt.i18nKey}`) : v != null && v !== '' ? String(v) : '—';
      }
      return (
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      );
    }
    switch (field.type) {
      case 'minutes':
        return (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={typeof v === 'number' ? v : ''}
              onChange={(e) => handleChange(field.key, Number(e.target.value))}
              disabled={disabled}
              sx={{
                '& .MuiSelect-select': {
                  fontWeight: disabled ? 600 : 400,
                  fontSize: '0.8rem',
                },
              }}
            >
              {MINUTE_OPTIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m >= 60 ? `${m / 60} hour${m > 60 ? 's' : ''}` : `${m} min`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'toggle':
        return (
          <Switch
            checked={!!v}
            onChange={(e) => {
              // Enabling a high-impact toggle → confirm first (parity with
              // GlobalRules). The dialog spells out the worker-facing
              // consequence + the always-on smart risk policy.
              if (e.target.checked && HIGH_IMPACT_TOGGLES.has(field.key)) {
                setConfirmKey(field.key);
                return;
              }
              handleChange(field.key, e.target.checked);
            }}
            disabled={disabled}
            size="small"
          />
        );
      case 'select':
        return (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={v ?? ''}
              onChange={(e) => {
                const opt = field.options?.find(
                  (o) => String(o.value) === String(e.target.value),
                );
                handleChange(field.key, opt?.value ?? e.target.value);
              }}
              disabled={disabled}
              sx={{
                '& .MuiSelect-select': {
                  fontWeight: disabled ? 600 : 400,
                  fontSize: '0.8rem',
                },
              }}
            >
              {field.options?.map((o) => (
                <MenuItem
                  key={String(o.value)}
                  value={o.value as string | number}
                >
                  {t(`attendance:ruleSections.options.${o.i18nKey}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'days': {
        const selectedDays = Array.isArray(v) ? (v as string[]) : [];
        return (
          <>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {DAY_OPTIONS.map((day) => {
              // Case-insensitive: a rule saved before the casing fix still shows its real state.
              const isOn = isRestDaySelected(selectedDays, day.value);
              return (
                <Chip
                  key={day.value}
                  label={t(`attendance:ruleSections.days.${day.i18nKey}`)}
                  size="small"
                  variant={isOn ? 'filled' : 'outlined'}
                  color={isOn ? 'primary' : 'default'}
                  onClick={
                    isEditing
                      ? () =>
                          handleChange(
                            field.key,
                            isOn
                              ? selectedDays.filter(
                                  (d) => d.trim().toLowerCase() !== day.value.toLowerCase(),
                                )
                              : [...selectedDays, day.value],
                          )
                      : undefined
                  }
                  sx={{
                    cursor: isEditing ? 'pointer' : 'default',
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              );
            })}
          </Box>
          {isEveryDayRest(selectedDays) && (
            <Alert severity="warning" variant="outlined" icon={false}
              sx={{ mt: 1, fontSize: '0.75rem', py: 0.25 }}>
              {t('attendance:ruleSections.allDaysRestWarning')}
            </Alert>
          )}
          </>
        );
      }
      default:
        return null;
    }
  };

  // ------- Per-field "differs from global" marker. Shown in the
  //          detail view header so operators see which rules this
  //          group has customized. -------

  const fieldDiffersFromGlobal = (field: RuleField): boolean => {
    if (!globalRule) return false;
    const g = (globalRule as unknown as Record<string, unknown>)[field.key];
    return JSON.stringify(values[field.key]) !== JSON.stringify(g);
  };

  // ------- List view -------

  if (!selectedDept) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          py: '12px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: 1.5,
            gap: 1.5,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.125rem',
                color: 'text.primary',
              }}
            >
              {t('attendance:groupRules.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('attendance:groupRules.subtitleDetail')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={t('common:actions.refresh')}>
              <IconButton
                size="small"
                onClick={fetchAll}
                disabled={loading}
                sx={{
                  border: '1px solid rgba(0,0,0,0.2)',
                  borderRadius: 1,
                  bgcolor: '#ffffff',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {savedScoringKeys.length > 0 && (
        <RecalcNotice changedScoringKeys={savedScoringKeys} onDismiss={() => setSavedScoringKeys([])} />
      )}
      {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <TextField
            variant="outlined"
            label={t('attendance:groupRules.filterLabel')}
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ width: 300, maxWidth: '100%' }}
          />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDepts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              '.MuiTablePagination-toolbar': { pl: 2 },
              '.MuiTablePagination-displayedRows': { m: 0 },
              '.MuiTablePagination-actions': { ml: 1 },
            }}
          />
        </Box>

        {loading ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              py: 6,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
          >
            <Table>
              <TableHead
                sx={{
                  bgcolor: 'grey.100',
                  '& .MuiTableCell-root': { fontWeight: 600 },
                }}
              >
                <TableRow>
                  <TableCell>{t('attendance:groupRules.colGroup', { defaultValue: 'Group' })}</TableCell>
                  <TableCell align="center">{t('attendance:groupRules.colFieldsOverridden', { defaultValue: 'Fields overridden' })}</TableCell>
                  <TableCell align="center">{t('attendance:timeEntries.colStatus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDepts.map((d) => {
                  const ov = overrides[d.id] ?? null;
                  const diff = computeDivergence(ov);
                  return (
                    <TableRow
                      key={d.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSelectDept(d.id)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {d.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: diff > 0 ? 'warning.main' : 'text.secondary',
                          }}
                        >
                          {ov ? diff : 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={ov ? 'Customized' : 'Inherited'}
                          size="small"
                          color={ov ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedDepts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {filterText
                          ? t('attendance:groupRules.noGroupsMatch')
                          : t('attendance:groupRules.noGroupsDefined')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1.5, display: 'block' }}
        >
          {t('attendance:groupRules.dontSeeGroup')}{' '}
          <Typography
            component="span"
            variant="caption"
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() =>
              (window.location.href = '/organization/groups')
            }
          >
            {t('attendance:groupRules.manageGroups')}
          </Typography>
        </Typography>

        <Snackbar
          open={!!toast}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="info"
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
          >
            {toast}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ------- Detail view -------

  const hasOverride = !!selectedOverride;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            size="small"
            onClick={handleBackToList}
            disabled={isEditing || saving}
            sx={{ mr: 1 }}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  color: 'text.primary',
                }}
              >
                {t('attendance:groupRules.overrideTitle', { name: selected?.name })}
              </Typography>
              <Chip
                label={hasOverride ? t('attendance:groupRules.chipCustomized') : t('attendance:groupRules.chipInherited')}
                size="small"
                color={hasOverride ? 'warning' : 'default'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {hasOverride
                ? t('attendance:groupRules.overrideHintCustomized')
                : t('attendance:groupRules.overrideHintInherited')}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {!isEditing && hasOverride && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={() => selected && setPendingRevert(selected)}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'text.secondary',
                borderColor: 'divider',
              }}
            >
              {t('attendance:rules.revertToGlobal')}
            </Button>
          )}
          {!isEditing && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={handleStartEdit}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'primary.main',
                borderColor: 'primary.main',
                bgcolor: '#ffffff',
                '&:hover': {
                  bgcolor: 'rgba(25,118,210,0.04)',
                  borderColor: 'primary.dark',
                },
              }}
            >
              {hasOverride ? t('attendance:rules.editOverride') : t('attendance:rules.createOverride')}
            </Button>
          )}
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {RULE_SECTIONS.map((section) => {
          const isWide = isWideRuleSection(section.i18nKey);
          return (
            <Grid
              item
              xs={12}
              md={isWide ? 12 : 6}
              key={section.i18nKey}
            >
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ px: 2.5, py: 1.25 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: 'text.primary',
                    }}
                  >
                    {t(`attendance:ruleSections.${section.i18nKey}.title`)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {t(`attendance:ruleSections.${section.i18nKey}.description`)}
                  </Typography>
                </Box>
                <CardContent sx={{ px: 2.5, pt: 0.5, pb: 2, flex: 1 }}>
                  {isWide ? (
                    <Grid container rowSpacing={1.5} columnSpacing={6}>
                      {section.fields.map((field) => (
                        <Grid item xs={12} sm={section.fields.length === 1 ? 12 : 6} key={field.key}>
                          <FieldRow
                            field={field}
                            renderField={renderField}
                            differsFromGlobal={fieldDiffersFromGlobal(field)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Stack spacing={1.5}>
                      {section.fields.map((field) => (
                        <FieldRow
                          key={field.key}
                          field={field}
                          renderField={renderField}
                          differsFromGlobal={fieldDiffersFromGlobal(field)}
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {isEditing && (
        <StickyFormActions
  primary={(
    <Button
    variant="contained"
    size="small"
    startIcon={saving ? <CircularProgress size={14} /> : <Save />}
    onClick={requestSave}
    disabled={saving}
    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
    >
    {hasOverride ? t('attendance:groupRules.saveOverride') : t('attendance:groupRules.createOverride')}
    </Button>
  )}
  secondary={(
    <Button
    variant="outlined"
    size="small"
    startIcon={<Cancel />}
    onClick={handleCancel}
    disabled={saving}
    sx={{
    borderRadius: 1,
    textTransform: 'none',
    fontSize: '0.875rem',
    color: 'text.secondary',
    borderColor: 'divider',
    }}
    >
    Cancel
    </Button>
  )}
/>
      )}

      <Dialog
        open={!!confirmKey}
        onClose={() => setConfirmKey(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700, fontSize: '1.02rem', pb: 1, pt: 2.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: 'rgba(237,108,2,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <InfoOutlined sx={{ fontSize: 19, color: 'warning.main' }} />
          </Box>
          {confirmKey ? t(`attendance:ruleConfirm.${confirmKey}.title`) : ''}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText
            sx={{ fontSize: '0.84rem', color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.55 }}
          >
            {confirmKey ? t(`attendance:ruleConfirm.${confirmKey}.body`) : ''}
          </DialogContentText>
          {confirmKey && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: 'rgba(237,108,2,0.07)',
                border: '1px solid',
                borderColor: 'rgba(237,108,2,0.25)',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              <PeopleAltOutlined sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0, mt: '1px' }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'warning.dark', letterSpacing: 0.2, mb: 0.25 }}>
                  {t('attendance:ruleConfirm.impactLabel')}
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', color: 'text.primary', lineHeight: 1.45 }}>
                  {t(`attendance:ruleConfirm.${confirmKey}.impact`)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setConfirmKey(null)}
            size="small"
            sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary', fontWeight: 600 }}
          >
            {t('attendance:ruleConfirm.keepOff')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => {
              if (confirmKey) handleChange(confirmKey, true);
              setConfirmKey(null);
            }}
            sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, px: 2 }}
          >
            {t('attendance:ruleConfirm.enable')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingRevert}
        tone="warning"
        title={t('attendance:groupRules.revertConfirmTitle', { defaultValue: 'Revert to global rules?' })}
        description="This group will inherit every rule from the global defaults. The override row is deleted — any custom values set for this group are lost."
        subject={
          pendingRevert && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingRevert.name}
            </Box>
          )
        }
        confirmLabel="Revert"
        confirmLoadingLabel="Reverting…"
        onConfirm={handleConfirmRevert}
        onClose={() => setPendingRevert(null)}
      />

      <RuleSaveConfirm
        open={confirmSave}
        changedScoringKeys={pendingScoringChanges}
        scopeLabel={selected?.name ?? t('attendance:ruleSave.scopeGroup')}
        onConfirm={performSave}
        onClose={() => setConfirmSave(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const FieldRow: React.FC<{
  field: RuleField;
  renderField: (f: RuleField) => React.ReactNode;
  differsFromGlobal: boolean;
}> = ({ field, renderField, differsFromGlobal }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      // Tint the background of any row whose value differs from the
      // global rule so operators can scan "what's actually different
      // about this group" without reading each field.
      ...(differsFromGlobal
        ? {
            bgcolor: 'rgba(237, 108, 2, 0.04)',
            borderRadius: 1,
            px: 1,
            mx: -1,
          }
        : {}),
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.primary' }}
        >
          {t(`attendance:ruleSections.fields.${field.i18nKey}.label`)}
        </Typography>
        {differsFromGlobal && (
          <Chip
            label="Overridden"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem', borderRadius: 0.5 }}
          />
        )}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}
      >
        {t(`attendance:ruleSections.fields.${field.i18nKey}.helper`)}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{renderField(field)}</Box>
  </Box>
);
};

export default GroupRules;
